import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { isValidCSharpInt } from 'src/app/common-components/helpers/csharp-max-integer-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { RolesFilterOption } from '../models/role-filter-option';
import { RoleOrderingOptions, RoleRegisterOptions } from '../models/role-register-options';
import { GetRoles, GetRolesVariables } from '../queries/generated/GetRoles';
import { getRolesQuery } from '../queries/roles.graphql';

@Injectable({ providedIn: 'root' })
export class RoleRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getRoles(
      endCursor: string | null,
      filters: RolesFilterOption[] | null,
      quickFilters: RoleRegisterOptions | null,
      orderingOptions: RoleOrderingOptions[] | null
   ): Observable<ExecutionResult<GetRoles>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertRoleFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            if (isValidCSharpInt(+searchText)) {
               subExpression.filterOptions.push({
                  field: 'UserRolesCount',
                  operation: FilterOperations.Contains,
                  value: searchText,
               });
            }
            subExpression.filterOptions.push({
               field: 'Description',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }
      const variables = this.getRolesVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetRoles, GetRolesVariables>({
         query: getRolesQuery,
         variables,
      });
   }

   private convertRoleFilterOption(option: RolesFilterOption): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: RolesFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'role':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.roleId;
                 })
               : option.value?.roleId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getRolesVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetRolesVariables {
      const variables: GetRolesVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
